import {matchPath} from 'react-router-dom'
import type {
  PageContextEntities,
  PageContextTimeRange,
  PageContextView,
  PageType,
  PublishedPageContext,
} from './types'

type RouteRule = {
  pattern: string
  page: PageType
  /** Path param -> entity key. */
  params?: Partial<Record<string, keyof PageContextEntities>>
}

// First match wins; patterns mirror Router.tsx. Pages not listed are `other`,
// still with whatever ids and time params the URL carries.
const ROUTES: RouteRule[] = [
  {pattern: '/signal-explorer', page: 'signal_explorer'},
  {
    pattern: '/dashboards/:dashboardId',
    page: 'custom_dashboard',
    params: {dashboardId: 'dashboard_id'},
  },
  {pattern: '/dashboard/:id', page: 'other', params: {id: 'dashboard_id'}},
  {pattern: '/line/:id', page: 'line_dashboard', params: {id: 'line_id'}},
  {pattern: '/line/:id/*', page: 'other', params: {id: 'line_id'}},
  {pattern: '/equipment/:id', page: 'equipment_dashboard', params: {id: 'equipment_id'}},
  {pattern: '/equipment/:id/*', page: 'other', params: {id: 'equipment_id'}},
  {pattern: '/incidents/:incidentId', page: 'incident', params: {incidentId: 'incident_id'}},
  {pattern: '/batch_traceability', page: 'batch_traceability'},
  {pattern: '/settings', page: 'settings'},
]

/**
 * A time query param as ISO. Accepts epoch milliseconds, epoch seconds and
 * anything `Date.parse` reads; returns undefined for the rest.
 */
export const toIso = (raw: string | null | undefined): string | undefined => {
  if (!raw) return undefined
  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    const n = Number(raw)
    const ms = Math.abs(n) >= 1e11 ? n : n * 1000
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
  }
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? undefined : new Date(parsed).toISOString()
}

/** A duration in ms as a relative preset, e.g. 86_400_000 -> `last_24h`. */
export const durationPreset = (ms: number): string | undefined => {
  if (!Number.isFinite(ms) || ms <= 0) return undefined
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60 || minutes % 60 !== 0) return `last_${minutes}m`
  const hours = minutes / 60
  if (hours <= 48 || hours % 24 !== 0) return `last_${hours}h`
  return `last_${hours / 24}d`
}

/**
 * The app's preset names as a contract preset: a Django duration such as
 * `1 00:00:00` (what TimeIntervalProvider keeps) becomes `last_24h`, named presets
 * (`day-so-far`) keep their name, and a custom window (`'null'`) has none.
 */
export const normalizePreset = (raw: string | null | undefined): string | null => {
  if (!raw || raw === 'null') return null
  const duration = /^(?:(\d+) )?(\d{1,2}):(\d{2}):(\d{2})$/.exec(raw)
  if (duration) {
    const [, days = '0', h, m, s] = duration
    const ms = (((Number(days) * 24 + Number(h)) * 60 + Number(m)) * 60 + Number(s)) * 1000
    return durationPreset(ms) ?? null
  }
  return raw.replace(/-/g, '_')
}

const timeRangeFromQuery = (query: URLSearchParams): PageContextTimeRange | undefined => {
  const start = toIso(query.get('t_start') ?? query.get('start_date'))
  const end = toIso(query.get('t_end') ?? query.get('end_date'))
  const duration = query.get('t_duration')
  const preset = query.has('time_range')
    ? normalizePreset(query.get('time_range'))
    : duration && /^\d+$/.test(duration)
      ? durationPreset(Number(duration))
      : duration
  const out: PageContextTimeRange = {}
  if (start) out.start = start
  if (end) out.end = end
  if (preset) out.preset = preset
  return Object.keys(out).length ? out : undefined
}

/**
 * The context any route has, from the router alone: the page type, the ids in
 * the path, the time params in the query, and `sub_menu` / `tab`. Rich pages
 * publish more on top (`usePublishPageContext`).
 */
export const routePageContext = (pathname: string, search: string): PublishedPageContext => {
  const query = new URLSearchParams(search)
  let page: PageType = 'other'
  const entities: PageContextEntities = {}
  for (const rule of ROUTES) {
    const match = matchPath({path: rule.pattern, end: true}, pathname)
    if (!match) continue
    page = rule.page
    for (const [param, key] of Object.entries(rule.params ?? {})) {
      const value = match.params[param]
      if (value && key && key !== 'batch_ids' && key !== 'signal_ids') entities[key] = value
    }
    break
  }

  const view: PageContextView = {}
  const tab = query.get('tab')
  const subMenu = query.get('sub_menu')
  if (tab) view.tab = tab
  if (subMenu) view.settings_section = subMenu

  const out: PublishedPageContext = {page}
  const timeRange = timeRangeFromQuery(query)
  if (Object.keys(entities).length) out.entities = entities
  if (timeRange) out.time_range = timeRange
  if (Object.keys(view).length) out.view = view
  return out
}

const dateIso = (value: Date | number | null | undefined): string | undefined => {
  if (value === null || value === undefined) return undefined
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/** A page's time window (TimeIntervalProvider's start, end and preset) in contract form. */
export const intervalTimeRange = (
  start: Date | number | null | undefined,
  end: Date | number | null | undefined,
  preset?: string | null,
): PageContextTimeRange => ({
  start: dateIso(start),
  end: dateIso(end),
  preset: normalizePreset(preset),
})
